/* Amborella — product page behaviour (progressive enhancement). */
(() => {
  const theme = window.theme || {};

  /* ---------- Variant selection ---------- */
  class ProductPage extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.formId = this.dataset.formId;
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.setupGallery();
      this.setupStickyMedia();
      this.guardAddToCartLabel();
    }

    /*
      ShipAid's "Add to Cart+" (app embed) relabels this button "Add to cart — not guaranteed" by
      writing into its first span. When it steps aside (the guarantee is already in the cart, or the
      variant is sold out) it pastes back the HTML it saved on page load, which doubles the price
      ("Add to cart · $9.00 · $9.00") and can bring back a stale label. Whenever ShipAid is not
      relabelling the button, put our own label back and drop the aria-label/title it left behind.
    */
    guardAddToCartLabel() {
      const button = this.querySelector('[data-add-to-cart]');
      const label = button && button.querySelector('[data-atc-label]');
      if (!button || !label || !('MutationObserver' in window)) return;
      this.atcButton = button;
      this.atcLabel = label;
      this.atcText = label.textContent.trim();
      const restore = () => {
        if (button.classList.contains('shipaid-atc-original-button')) return;
        if (label.children.length || label.textContent.trim() !== this.atcText) label.textContent = this.atcText;
        if (button.hasAttribute('aria-label')) button.removeAttribute('aria-label');
        if (button.hasAttribute('title')) button.removeAttribute('title');
      };
      this.atcObserver = new MutationObserver(restore);
      this.atcObserver.observe(button, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'aria-label', 'title']
      });
    }

    disconnectedCallback() {
      if (this.stickyCleanup) this.stickyCleanup();
      if (this.atcObserver) this.atcObserver.disconnect();
    }

    /*
      Desktop: the media column follows the page. A gallery taller than the window scrolls
      with the page until its bottom edge is in view, then stays; scrolling back up reveals
      its top again before it pins below the header. Shorter galleries simply pin below the header.
    */
    setupStickyMedia() {
      const column = this.querySelector('.product__media-column');
      if (!column || !('ResizeObserver' in window)) return;
      const desktop = window.matchMedia('(min-width: 990px)');
      const gap = 24;
      let top = null;
      let lastY = window.scrollY;
      let queued = false;

      const apply = () => {
        queued = false;
        const y = window.scrollY;
        if (!desktop.matches) {
          column.style.removeProperty('--media-sticky-top');
          top = null;
          lastY = y;
          return;
        }
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-offset')) || 0;
        const max = header + gap;
        const min = Math.min(max, window.innerHeight - column.offsetHeight - gap);
        top = top === null ? max : top - (y - lastY);
        top = Math.min(max, Math.max(min, top));
        lastY = y;
        column.style.setProperty('--media-sticky-top', `${Math.round(top)}px`);
      };
      const request = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(apply);
      };

      const observer = new ResizeObserver(request);
      observer.observe(column);
      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request);
      desktop.addEventListener('change', request);
      this.stickyCleanup = () => {
        observer.disconnect();
        window.removeEventListener('scroll', request);
        window.removeEventListener('resize', request);
        desktop.removeEventListener('change', request);
      };
      apply();
    }

    selectedOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-index]'));
      return fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('[data-option-value]:checked');
        return checked ? checked.value : null;
      });
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-option-value]')) {
        const fieldset = target.closest('fieldset');
        const label = fieldset && fieldset.querySelector('[data-selected-value]');
        if (label) label.textContent = target.value;
        this.updateVariant();
      }
      if (target.matches('[data-selling-plan-choice]')) {
        const input = this.querySelector('[data-selling-plan-input]');
        if (input) input.value = target.value;
      }
    }

    onClick(event) {
      const step = event.target.closest('[data-quantity-step]');
      if (step) {
        const input = this.querySelector('input[name="quantity"]');
        if (!input) return;
        const increment = Number(input.step) || 1;
        const min = Number(input.min) || 1;
        const max = input.max ? Number(input.max) : Infinity;
        const next = Number(input.value) + Number(step.dataset.quantityStep) * increment;
        input.value = Math.min(max, Math.max(min, next));
      }
    }

    updateVariant() {
      const options = this.selectedOptions();
      const variant = this.product.variants.find((v) => v.options.every((value, index) => value === options[index]));
      const input = this.querySelector('[data-variant-input]');
      const button = this.querySelector('[data-add-to-cart]');
      const label = this.querySelector('[data-atc-label]');
      const price = this.querySelector('[data-atc-price]');

      if (!variant) {
        if (input) input.disabled = true;
        if (button) button.disabled = true;
        this.atcText = theme.strings.unavailable;
        if (label) label.textContent = this.atcText;
        return;
      }

      if (input) {
        input.value = variant.id;
        input.disabled = false;
      }
      if (button) button.disabled = !variant.available;
      this.atcText = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
      if (label) label.textContent = this.atcText;
      if (price) price.textContent = variant.price;

      const current = this.querySelector('[data-price-current]');
      const compare = this.querySelector('[data-price-compare]');
      if (current) {
        current.textContent = variant.price;
        current.classList.toggle('price__sale', Boolean(variant.compare));
      }
      if (compare) {
        compare.textContent = variant.compare || '';
        compare.hidden = !variant.compare;
      }
      this.querySelectorAll('[data-sale-label], [data-regular-label]').forEach((el) => {
        el.hidden = !variant.compare;
      });
      const unit = this.querySelector('[data-price-unit]');
      if (unit) {
        unit.textContent = variant.unit || '';
        unit.hidden = !variant.unit;
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      if (variant.mediaId) this.showMedia(variant.mediaId);

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    /* Gallery: desktop grid, mobile swipe track with thumbnails. */
    setupGallery() {
      this.track = this.querySelector('[data-gallery-track]');
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb-index]'));
      if (!this.track) return;
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.scrollToIndex(Number(thumb.dataset.thumbIndex)));
      });
      const onScroll = () => {
        const slides = Array.from(this.track.children);
        const left = this.track.scrollLeft;
        let active = 0;
        slides.forEach((slide, index) => {
          if (Math.abs(slide.offsetLeft - this.track.offsetLeft - left) < slide.offsetWidth / 2) active = index;
        });
        this.setActiveThumb(active);
      };
      this.track.addEventListener('scroll', theme.utils ? theme.utils.debounce(onScroll, 60) : onScroll, { passive: true });
    }

    setActiveThumb(index) {
      this.thumbs.forEach((thumb) => {
        const active = Number(thumb.dataset.thumbIndex) === index;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    scrollToIndex(index) {
      const slide = this.track.children[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      this.setActiveThumb(index);
    }

    showMedia(mediaId) {
      const item = this.track && this.track.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;
      if (window.matchMedia('(min-width: 990px)').matches) {
        const main = this.track.querySelector('.gallery__item--main');
        if (main === item) return;
        const largeSizes = '(min-width: 1200px) 620px, (min-width: 990px) 50vw, 312px';
        const smallSizes = '(min-width: 1200px) 304px, (min-width: 990px) 25vw, 312px';
        if (main) {
          main.classList.remove('gallery__item--main');
          const mainImg = main.querySelector('img');
          if (mainImg) mainImg.sizes = smallSizes;
        }
        item.classList.add('gallery__item--main');
        const img = item.querySelector('img');
        if (img) img.sizes = largeSizes;
        this.track.prepend(item);
      } else {
        this.scrollToIndex(Array.from(this.track.children).indexOf(item));
      }
    }
  }
  if (!customElements.get('product-page')) customElements.define('product-page', ProductPage);

  /* ---------- Delivery estimate ---------- */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const processing = Number(this.dataset.processing || 0);
      const min = Number(this.dataset.min || 3);
      const max = Number(this.dataset.max || min);
      const cutoff = Number(this.dataset.cutoff || 14);
      const now = new Date();
      let start = new Date(now);
      if (now.getHours() >= cutoff) start = this.addBusinessDays(start, 1);
      start = this.addBusinessDays(start, processing);
      const from = this.addBusinessDays(start, min);
      const to = this.addBusinessDays(start, max);
      const locale = this.dataset.locale || undefined;
      const format = (date) => date.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' });
      const html = min === max
        ? this.dataset.template.replace('[date]', format(to))
        : this.dataset.templateRange.replace('[start]', format(from)).replace('[end]', format(to));
      this.innerHTML = html;
      this.hidden = false;
    }

    addBusinessDays(date, days) {
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        const day = result.getDay();
        if (day !== 0 && day !== 6) added += 1;
      }
      while (result.getDay() === 0 || result.getDay() === 6) result.setDate(result.getDate() + 1);
      return result;
    }
  }
  if (!customElements.get('delivery-estimate')) customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------- Frequently bought together ---------- */
  class FbtBundle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-fbt-submit]');
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-fbt-variant]')) {
          const item = event.target.closest('[data-fbt-item]');
          const option = event.target.selectedOptions[0];
          item.dataset.variantId = event.target.value;
          item.dataset.priceCents = option.dataset.priceCents;
        }
        this.update();
      });
      this.button && this.button.addEventListener('click', () => this.submit());
      document.addEventListener('variant:change', (event) => {
        const current = this.querySelector('[data-fbt-current]');
        if (!current) return;
        const { variant } = event.detail;
        current.dataset.variantId = variant.id;
        current.dataset.priceCents = variant.priceCents;
        const price = this.querySelector('[data-fbt-current-price]');
        if (price) price.textContent = variant.price;
        this.update();
      });
      this.update();
    }

    selected() {
      return Array.from(this.querySelectorAll('[data-fbt-item]')).filter((item) => {
        if (item.hasAttribute('data-fbt-current')) return true;
        const toggle = item.querySelector('[data-fbt-toggle]');
        return toggle && toggle.checked;
      });
    }

    update() {
      const items = this.selected();
      const total = items.reduce((sum, item) => sum + Number(item.dataset.priceCents || 0), 0);
      const label = this.querySelector('[data-fbt-label]');
      const totalEl = this.querySelector('[data-fbt-total]');
      if (label) {
        label.textContent = items.length === 1 ? label.dataset.labelOne : label.dataset.labelOther.replace('99', items.length);
      }
      if (totalEl) totalEl.textContent = this.formatMoney(total);
    }

    formatMoney(cents) {
      const format = theme.moneyFormat || '${{amount}}';
      const key = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
      const rules = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_no_decimals_with_space_separator: [0, ' ', ''],
        amount_with_space_separator: [2, ' ', ','],
        amount_with_period_and_space_separator: [2, ' ', '.']
      };
      const [decimals, thousands, point] = rules[key] || rules.amount;
      const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
      const number = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? point + fraction : '');
      const el = document.createElement('div');
      el.innerHTML = format.replace(/\{\{\s*\w+\s*\}\}/, number);
      return el.textContent;
    }

    async submit() {
      const items = this.selected().map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }));
      const error = this.querySelector('[data-form-error]');
      if (error) error.hidden = true;
      this.button.classList.add('is-loading');
      try {
        await theme.cart.addItems(items);
      } catch (e) {
        if (error) {
          error.textContent = e.message;
          error.hidden = false;
        }
      } finally {
        this.button.classList.remove('is-loading');
      }
    }
  }
  if (!customElements.get('fbt-bundle')) customElements.define('fbt-bundle', FbtBundle);
})();
